import { makeAutoObservable } from "mobx";
import { RootStore } from "@intrasync/stores";
import {
  AutomationWorkflow,
  WorkflowExecution,
  ShippingInvoiceRule,
  RecurringInvoice,
  BatchProcess,
  MonthEndClose,
  AutoPostingRule,
  WorkflowType,
  WorkflowStatus,
  WorkflowExecutionStatus,
  RecurrenceFrequency,
  WorkflowTrigger,
  WorkflowAction,
  WorkflowCondition,
  RecurringInvoiceLineItem,
  MonthEndCloseStep,
  AutoPostingRuleLine,
} from "../types";

export default class AutomationWorkflowsStore {
  rootStore: RootStore;

  // UI State
  selectedTab: number = 0;
  selectedWorkflowId: string | null = null;
  selectedExecutionId: string | null = null;
  isLoading: boolean = false;

  // Dialog States
  workflowDialogOpen: boolean = false;
  executionDialogOpen: boolean = false;
  shippingRuleDialogOpen: boolean = false;
  recurringInvoiceDialogOpen: boolean = false;
  batchProcessDialogOpen: boolean = false;
  monthEndCloseDialogOpen: boolean = false;
  postingRuleDialogOpen: boolean = false;

  // Form Data
  workflowFormData: Partial<AutomationWorkflow> = this.getInitialWorkflowFormData();
  shippingRuleFormData: Partial<ShippingInvoiceRule> = this.getInitialShippingRuleFormData();
  recurringInvoiceFormData: Partial<RecurringInvoice> = this.getInitialRecurringInvoiceFormData();
  postingRuleFormData: Partial<AutoPostingRule> = this.getInitialPostingRuleFormData();

  // Edit Mode
  isEditMode: boolean = false;

  // Filters
  workflowTypeFilter: WorkflowType | "all" = "all";
  workflowStatusFilter: WorkflowStatus | "all" = "all";
  executionStatusFilter: WorkflowExecutionStatus | "all" = "all";
  dateRangeFilter: { startDate: string; endDate: string } = {
    startDate: "",
    endDate: "",
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ===================================================================
  // SERVICE STORE GETTERS
  // ===================================================================

  get automationWorkflowsStore() {
    return this.rootStore["automationWorkflowsStore" as keyof RootStore] as {
      entities: AutomationWorkflow[];
      find: (params?: any) => Promise<any>;
      get: (id: string) => Promise<any>;
      create: (data: any) => Promise<any>;
      patch: (id: string, data: any) => Promise<any>;
      remove: (id: string) => Promise<any>;
    };
  }

  get workflowExecutionsStore() {
    return this.rootStore["workflowExecutionsStore" as keyof RootStore] as {
      entities: WorkflowExecution[];
      find: (params?: any) => Promise<any>;
      get: (id: string) => Promise<any>;
      create: (data: any) => Promise<any>;
    };
  }

  get shippingInvoiceRulesStore() {
    return this.rootStore["shippingInvoiceRulesStore" as keyof RootStore] as {
      entities: ShippingInvoiceRule[];
      find: (params?: any) => Promise<any>;
      create: (data: any) => Promise<any>;
      patch: (id: string, data: any) => Promise<any>;
      remove: (id: string) => Promise<any>;
    };
  }

  get recurringInvoicesStore() {
    return this.rootStore["recurringInvoicesStore" as keyof RootStore] as {
      entities: RecurringInvoice[];
      find: (params?: any) => Promise<any>;
      create: (data: any) => Promise<any>;
      patch: (id: string, data: any) => Promise<any>;
      remove: (id: string) => Promise<any>;
    };
  }

  get batchProcessesStore() {
    return this.rootStore["batchProcessesStore" as keyof RootStore] as {
      entities: BatchProcess[];
      find: (params?: any) => Promise<any>;
      get: (id: string) => Promise<any>;
      create: (data: any) => Promise<any>;
    };
  }

  get monthEndClosesStore() {
    return this.rootStore["monthEndClosesStore" as keyof RootStore] as {
      entities: MonthEndClose[];
      find: (params?: any) => Promise<any>;
      get: (id: string) => Promise<any>;
      create: (data: any) => Promise<any>;
      patch: (id: string, data: any) => Promise<any>;
    };
  }

  get autoPostingRulesStore() {
    return this.rootStore["autoPostingRulesStore" as keyof RootStore] as {
      entities: AutoPostingRule[];
      find: (params?: any) => Promise<any>;
      create: (data: any) => Promise<any>;
      patch: (id: string, data: any) => Promise<any>;
      remove: (id: string) => Promise<any>;
    };
  }

  get invoicesStore() {
    return this.rootStore["invoicesStore" as keyof RootStore] as {
      entities: any[];
      create: (data: any) => Promise<any>;
    };
  }

  get customersStore() {
    return this.rootStore["customersStore" as keyof RootStore] as {
      entities: any[];
    };
  }

  // ===================================================================
  // COMPUTED PROPERTIES
  // ===================================================================

  get workflows(): AutomationWorkflow[] {
    let workflows = this.automationWorkflowsStore?.entities || [];

    if (this.workflowTypeFilter !== "all") {
      workflows = workflows.filter((w) => w.workflowType === this.workflowTypeFilter);
    }

    if (this.workflowStatusFilter !== "all") {
      workflows = workflows.filter((w) => w.status === this.workflowStatusFilter);
    }

    return workflows.sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  get executions(): WorkflowExecution[] {
    let executions = this.workflowExecutionsStore?.entities || [];

    if (this.executionStatusFilter !== "all") {
      executions = executions.filter((e) => e.status === this.executionStatusFilter);
    }

    if (this.dateRangeFilter.startDate) {
      executions = executions.filter(
        (e) => new Date(e.startedAt) >= new Date(this.dateRangeFilter.startDate)
      );
    }

    if (this.dateRangeFilter.endDate) {
      executions = executions.filter(
        (e) => new Date(e.startedAt) <= new Date(this.dateRangeFilter.endDate)
      );
    }

    return executions.sort((a, b) =>
      new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
    );
  }

  get shippingRules(): ShippingInvoiceRule[] {
    return (this.shippingInvoiceRulesStore?.entities || []).sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  get recurringInvoices(): RecurringInvoice[] {
    return (this.recurringInvoicesStore?.entities || []).sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  get batchProcesses(): BatchProcess[] {
    return (this.batchProcessesStore?.entities || []).sort((a, b) =>
      new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
    );
  }

  get monthEndCloses(): MonthEndClose[] {
    return (this.monthEndClosesStore?.entities || []).sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  get postingRules(): AutoPostingRule[] {
    return (this.autoPostingRulesStore?.entities || []).sort((a, b) =>
      b.priority - a.priority
    );
  }

  get selectedWorkflow(): AutomationWorkflow | null {
    if (!this.selectedWorkflowId) return null;
    return this.workflows.find((w) => w.id === this.selectedWorkflowId) || null;
  }

  get selectedExecution(): WorkflowExecution | null {
    if (!this.selectedExecutionId) return null;
    return this.executions.find((e) => e.id === this.selectedExecutionId) || null;
  }

  // Statistics
  get workflowStats() {
    const workflows = this.automationWorkflowsStore?.entities || [];
    return {
      total: workflows.length,
      active: workflows.filter((w) => w.isActive && w.status === WorkflowStatus.ACTIVE).length,
      inactive: workflows.filter((w) => !w.isActive || w.status === WorkflowStatus.INACTIVE).length,
      error: workflows.filter((w) => w.status === WorkflowStatus.ERROR).length,
      avgExecutionCount: workflows.length > 0
        ? workflows.reduce((sum, w) => sum + w.executionCount, 0) / workflows.length
        : 0,
      successRate: workflows.length > 0
        ? (workflows.reduce((sum, w) => sum + w.successCount, 0) /
           workflows.reduce((sum, w) => sum + w.executionCount, 0)) * 100 || 0
        : 0,
    };
  }

  get executionStats() {
    const executions = this.workflowExecutionsStore?.entities || [];
    return {
      total: executions.length,
      completed: executions.filter((e) => e.status === WorkflowExecutionStatus.COMPLETED).length,
      failed: executions.filter((e) => e.status === WorkflowExecutionStatus.FAILED).length,
      running: executions.filter((e) => e.status === WorkflowExecutionStatus.RUNNING).length,
      avgDuration: executions.filter((e) => e.duration).length > 0
        ? executions.filter((e) => e.duration).reduce((sum, e) => sum + (e.duration || 0), 0) /
          executions.filter((e) => e.duration).length
        : 0,
    };
  }

  // ===================================================================
  // ACTIONS - UI STATE
  // ===================================================================

  setSelectedTab = (tab: number) => {
    this.selectedTab = tab;
  };

  setSelectedWorkflowId = (id: string | null) => {
    this.selectedWorkflowId = id;
  };

  setSelectedExecutionId = (id: string | null) => {
    this.selectedExecutionId = id;
  };

  setWorkflowTypeFilter = (filter: WorkflowType | "all") => {
    this.workflowTypeFilter = filter;
  };

  setWorkflowStatusFilter = (filter: WorkflowStatus | "all") => {
    this.workflowStatusFilter = filter;
  };

  setExecutionStatusFilter = (filter: WorkflowExecutionStatus | "all") => {
    this.executionStatusFilter = filter;
  };

  setDateRangeFilter = (startDate: string, endDate: string) => {
    this.dateRangeFilter = { startDate, endDate };
  };

  // ===================================================================
  // ACTIONS - WORKFLOW MANAGEMENT
  // ===================================================================

  openWorkflowDialog = (workflow?: AutomationWorkflow) => {
    if (workflow) {
      this.workflowFormData = { ...workflow };
      this.isEditMode = true;
    } else {
      this.workflowFormData = this.getInitialWorkflowFormData();
      this.isEditMode = false;
    }
    this.workflowDialogOpen = true;
  };

  closeWorkflowDialog = () => {
    this.workflowDialogOpen = false;
    this.workflowFormData = this.getInitialWorkflowFormData();
    this.isEditMode = false;
  };

  updateWorkflowFormData = (field: keyof AutomationWorkflow, value: any) => {
    (this.workflowFormData as any)[field] = value;
  };

  saveWorkflow = async () => {
    try {
      const workflowData = {
        ...this.workflowFormData,
        executionCount: 0,
        successCount: 0,
        failureCount: 0,
      };

      if (this.isEditMode && this.workflowFormData.id) {
        await this.automationWorkflowsStore.patch(
          this.workflowFormData.id,
          workflowData
        );
      } else {
        await this.automationWorkflowsStore.create(workflowData);
      }

      this.closeWorkflowDialog();
    } catch (error) {
      console.error("Failed to save workflow:", error);
      throw error;
    }
  };

  deleteWorkflow = async (id: string) => {
    try {
      await this.automationWorkflowsStore.remove(id);
    } catch (error) {
      console.error("Failed to delete workflow:", error);
      throw error;
    }
  };

  toggleWorkflowStatus = async (id: string) => {
    try {
      const workflow = this.workflows.find((w) => w.id === id);
      if (!workflow) return;

      await this.automationWorkflowsStore.patch(id, {
        isActive: !workflow.isActive,
        status: !workflow.isActive ? WorkflowStatus.ACTIVE : WorkflowStatus.INACTIVE,
      });
    } catch (error) {
      console.error("Failed to toggle workflow status:", error);
      throw error;
    }
  };

  executeWorkflow = async (id: string) => {
    try {
      const workflow = this.workflows.find((w) => w.id === id);
      if (!workflow) return;

      // Create execution record
      await this.workflowExecutionsStore.create({
        workflowId: id,
        workflowName: workflow.name,
        status: WorkflowExecutionStatus.PENDING,
        triggeredBy: "user",
        triggeredByUserId: this.rootStore.authStore?.currentUser?.id,
        startedAt: new Date().toISOString(),
        retryCount: 0,
      });

      // In real implementation, this would trigger backend workflow execution
      console.log(`Executing workflow ${id}`);
    } catch (error) {
      console.error("Failed to execute workflow:", error);
      throw error;
    }
  };

  // ===================================================================
  // ACTIONS - SHIPPING INVOICE RULES
  // ===================================================================

  openShippingRuleDialog = (rule?: ShippingInvoiceRule) => {
    if (rule) {
      this.shippingRuleFormData = { ...rule };
      this.isEditMode = true;
    } else {
      this.shippingRuleFormData = this.getInitialShippingRuleFormData();
      this.isEditMode = false;
    }
    this.shippingRuleDialogOpen = true;
  };

  closeShippingRuleDialog = () => {
    this.shippingRuleDialogOpen = false;
    this.shippingRuleFormData = this.getInitialShippingRuleFormData();
    this.isEditMode = false;
  };

  updateShippingRuleFormData = (field: keyof ShippingInvoiceRule, value: any) => {
    (this.shippingRuleFormData as any)[field] = value;
  };

  saveShippingRule = async () => {
    try {
      if (this.isEditMode && this.shippingRuleFormData.id) {
        await this.shippingInvoiceRulesStore.patch(
          this.shippingRuleFormData.id,
          this.shippingRuleFormData
        );
      } else {
        await this.shippingInvoiceRulesStore.create(this.shippingRuleFormData);
      }

      this.closeShippingRuleDialog();
    } catch (error) {
      console.error("Failed to save shipping rule:", error);
      throw error;
    }
  };

  deleteShippingRule = async (id: string) => {
    try {
      await this.shippingInvoiceRulesStore.remove(id);
    } catch (error) {
      console.error("Failed to delete shipping rule:", error);
      throw error;
    }
  };

  // ===================================================================
  // ACTIONS - RECURRING INVOICES
  // ===================================================================

  openRecurringInvoiceDialog = (invoice?: RecurringInvoice) => {
    if (invoice) {
      this.recurringInvoiceFormData = { ...invoice };
      this.isEditMode = true;
    } else {
      this.recurringInvoiceFormData = this.getInitialRecurringInvoiceFormData();
      this.isEditMode = false;
    }
    this.recurringInvoiceDialogOpen = true;
  };

  closeRecurringInvoiceDialog = () => {
    this.recurringInvoiceDialogOpen = false;
    this.recurringInvoiceFormData = this.getInitialRecurringInvoiceFormData();
    this.isEditMode = false;
  };

  updateRecurringInvoiceFormData = (field: keyof RecurringInvoice, value: any) => {
    (this.recurringInvoiceFormData as any)[field] = value;
  };

  saveRecurringInvoice = async () => {
    try {
      // Calculate totals
      const lineItems = this.recurringInvoiceFormData.lineItems || [];
      const amount = lineItems.reduce((sum, item) => sum + item.amount, 0);
      const taxAmount = lineItems.reduce((sum, item) =>
        sum + (item.taxable ? item.amount * 0.08 : 0), 0
      );
      const totalAmount = amount + taxAmount;

      const invoiceData = {
        ...this.recurringInvoiceFormData,
        amount,
        taxAmount,
        totalAmount,
        invoiceCount: 0,
      };

      if (this.isEditMode && this.recurringInvoiceFormData.id) {
        await this.recurringInvoicesStore.patch(
          this.recurringInvoiceFormData.id,
          invoiceData
        );
      } else {
        await this.recurringInvoicesStore.create(invoiceData);
      }

      this.closeRecurringInvoiceDialog();
    } catch (error) {
      console.error("Failed to save recurring invoice:", error);
      throw error;
    }
  };

  deleteRecurringInvoice = async (id: string) => {
    try {
      await this.recurringInvoicesStore.remove(id);
    } catch (error) {
      console.error("Failed to delete recurring invoice:", error);
      throw error;
    }
  };

  addRecurringInvoiceLineItem = () => {
    const lineItems = this.recurringInvoiceFormData.lineItems || [];
    this.recurringInvoiceFormData.lineItems = [
      ...lineItems,
      {
        id: `line-${Date.now()}`,
        description: "",
        quantity: 1,
        unitPrice: 0,
        amount: 0,
        accountId: "",
        taxable: true,
      },
    ];
  };

  removeRecurringInvoiceLineItem = (index: number) => {
    const lineItems = this.recurringInvoiceFormData.lineItems || [];
    this.recurringInvoiceFormData.lineItems = lineItems.filter((_, i) => i !== index);
  };

  updateRecurringInvoiceLineItem = (index: number, field: keyof RecurringInvoiceLineItem, value: any) => {
    const lineItems = [...(this.recurringInvoiceFormData.lineItems || [])];
    if (lineItems[index]) {
      (lineItems[index] as any)[field] = value;

      // Auto-calculate amount
      if (field === "quantity" || field === "unitPrice") {
        lineItems[index].amount = lineItems[index].quantity * lineItems[index].unitPrice;
      }

      this.recurringInvoiceFormData.lineItems = lineItems;
    }
  };

  // ===================================================================
  // ACTIONS - BATCH PROCESSING
  // ===================================================================

  openBatchProcessDialog = () => {
    this.batchProcessDialogOpen = true;
  };

  closeBatchProcessDialog = () => {
    this.batchProcessDialogOpen = false;
  };

  startBatchProcess = async (batchType: "payment" | "invoice" | "journal_entry" | "month_end_close", itemIds: string[]) => {
    try {
      await this.batchProcessesStore.create({
        batchType,
        name: `${batchType} batch - ${new Date().toLocaleString()}`,
        description: `Batch processing ${itemIds.length} items`,
        status: WorkflowExecutionStatus.PENDING,
        totalItems: itemIds.length,
        processedItems: 0,
        successfulItems: 0,
        failedItems: 0,
        progress: 0,
        startedBy: this.rootStore.authStore?.currentUser?.id,
        startedAt: new Date().toISOString(),
      });

      this.closeBatchProcessDialog();
    } catch (error) {
      console.error("Failed to start batch process:", error);
      throw error;
    }
  };

  // ===================================================================
  // ACTIONS - MONTH-END CLOSE
  // ===================================================================

  openMonthEndCloseDialog = () => {
    this.monthEndCloseDialogOpen = true;
  };

  closeMonthEndCloseDialog = () => {
    this.monthEndCloseDialogOpen = false;
  };

  startMonthEndClose = async (periodMonth: string) => {
    try {
      const steps: Omit<MonthEndCloseStep, "id" | "monthEndCloseId" | "createdAt" | "updatedAt">[] = [
        { stepNumber: 1, stepName: "Reconcile Bank Accounts", stepType: "reconciliation", status: WorkflowExecutionStatus.PENDING, isRequired: true },
        { stepNumber: 2, stepName: "Review Accounts Receivable", stepType: "review", status: WorkflowExecutionStatus.PENDING, isRequired: true },
        { stepNumber: 3, stepName: "Review Accounts Payable", stepType: "review", status: WorkflowExecutionStatus.PENDING, isRequired: true },
        { stepNumber: 4, stepName: "Accrue Expenses", stepType: "accrual", status: WorkflowExecutionStatus.PENDING, isRequired: true },
        { stepNumber: 5, stepName: "Depreciation Calculation", stepType: "calculation", status: WorkflowExecutionStatus.PENDING, isRequired: true },
        { stepNumber: 6, stepName: "Review Journal Entries", stepType: "review", status: WorkflowExecutionStatus.PENDING, isRequired: true },
        { stepNumber: 7, stepName: "Generate Financial Statements", stepType: "report", status: WorkflowExecutionStatus.PENDING, isRequired: true },
        { stepNumber: 8, stepName: "Final Review", stepType: "review", status: WorkflowExecutionStatus.PENDING, isRequired: true },
      ];

      await this.monthEndClosesStore.create({
        periodMonth,
        status: "not_started",
        steps,
        totalSteps: steps.length,
        completedSteps: 0,
        errorSteps: 0,
        isClosed: false,
      });

      this.closeMonthEndCloseDialog();
    } catch (error) {
      console.error("Failed to start month-end close:", error);
      throw error;
    }
  };

  // ===================================================================
  // ACTIONS - AUTO-POSTING RULES
  // ===================================================================

  openPostingRuleDialog = (rule?: AutoPostingRule) => {
    if (rule) {
      this.postingRuleFormData = { ...rule };
      this.isEditMode = true;
    } else {
      this.postingRuleFormData = this.getInitialPostingRuleFormData();
      this.isEditMode = false;
    }
    this.postingRuleDialogOpen = true;
  };

  closePostingRuleDialog = () => {
    this.postingRuleDialogOpen = false;
    this.postingRuleFormData = this.getInitialPostingRuleFormData();
    this.isEditMode = false;
  };

  updatePostingRuleFormData = (field: keyof AutoPostingRule, value: any) => {
    (this.postingRuleFormData as any)[field] = value;
  };

  savePostingRule = async () => {
    try {
      if (this.isEditMode && this.postingRuleFormData.id) {
        await this.autoPostingRulesStore.patch(
          this.postingRuleFormData.id,
          this.postingRuleFormData
        );
      } else {
        await this.autoPostingRulesStore.create(this.postingRuleFormData);
      }

      this.closePostingRuleDialog();
    } catch (error) {
      console.error("Failed to save posting rule:", error);
      throw error;
    }
  };

  deletePostingRule = async (id: string) => {
    try {
      await this.autoPostingRulesStore.remove(id);
    } catch (error) {
      console.error("Failed to delete posting rule:", error);
      throw error;
    }
  };

  addPostingRuleLine = () => {
    const lines = this.postingRuleFormData.journalEntryTemplate?.lines || [];
    this.postingRuleFormData.journalEntryTemplate = {
      description: this.postingRuleFormData.journalEntryTemplate?.description || "",
      lines: [
        ...lines,
        {
          accountId: "",
          accountType: "debit",
          amountSource: "totalAmount",
        },
      ],
    };
  };

  removePostingRuleLine = (index: number) => {
    const lines = this.postingRuleFormData.journalEntryTemplate?.lines || [];
    this.postingRuleFormData.journalEntryTemplate = {
      description: this.postingRuleFormData.journalEntryTemplate?.description || "",
      lines: lines.filter((_, i) => i !== index),
    };
  };

  // ===================================================================
  // INITIAL FORM DATA
  // ===================================================================

  getInitialWorkflowFormData(): Partial<AutomationWorkflow> {
    return {
      workflowType: WorkflowType.SHIPPING_TO_INVOICE,
      name: "",
      description: "",
      status: WorkflowStatus.INACTIVE,
      triggerConfig: {
        triggerType: "event",
      },
      actionConfig: {
        actionType: "create_invoice",
        parameters: {},
      },
      conditions: [],
      isActive: false,
    };
  }

  getInitialShippingRuleFormData(): Partial<ShippingInvoiceRule> {
    return {
      name: "",
      description: "",
      isActive: true,
      paymentTerms: 30,
      autoSend: false,
      requireQCApproval: false,
      billingType: "full",
    };
  }

  getInitialRecurringInvoiceFormData(): Partial<RecurringInvoice> {
    return {
      customerId: "",
      templateName: "",
      description: "",
      frequency: RecurrenceFrequency.MONTHLY,
      startDate: new Date().toISOString().split("T")[0],
      nextInvoiceDate: new Date().toISOString().split("T")[0],
      lineItems: [],
      paymentTerms: 30,
      autoSend: false,
      isActive: true,
    };
  }

  getInitialPostingRuleFormData(): Partial<AutoPostingRule> {
    return {
      ruleName: "",
      description: "",
      isActive: true,
      triggerEvent: "",
      sourceEntityType: "invoice",
      journalEntryTemplate: {
        description: "",
        lines: [],
      },
      conditions: [],
      priority: 100,
    };
  }

  // ===================================================================
  // DATA LOADING
  // ===================================================================

  loadData = async () => {
    this.isLoading = true;
    try {
      await Promise.all([
        this.automationWorkflowsStore?.find(),
        this.workflowExecutionsStore?.find(),
        this.shippingInvoiceRulesStore?.find(),
        this.recurringInvoicesStore?.find(),
        this.batchProcessesStore?.find(),
        this.monthEndClosesStore?.find(),
        this.autoPostingRulesStore?.find(),
      ]);
    } catch (error) {
      console.error("Failed to load automation data:", error);
    } finally {
      this.isLoading = false;
    }
  };

  // ===================================================================
  // LIFECYCLE
  // ===================================================================

  cleanup = () => {
    this.selectedTab = 0;
    this.selectedWorkflowId = null;
    this.selectedExecutionId = null;
    this.workflowFormData = this.getInitialWorkflowFormData();
    this.shippingRuleFormData = this.getInitialShippingRuleFormData();
    this.recurringInvoiceFormData = this.getInitialRecurringInvoiceFormData();
    this.postingRuleFormData = this.getInitialPostingRuleFormData();
  };
}
